/**
 * src/utils/supabase.ts
 * Supabase クライアント & ヘルパー関数 – 完全版
 * --------------------------------------------------
 * 1. persistSession / autoRefreshToken を有効化
 * 2. 開発中のみ window.supabase に公開（DevTools から確認可）
 * 3. users / user_credits 用ユーティリティを堅牢化
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { SUPABASE_ANON_KEY, SUPABASE_URL } from './constants';

/* ------------------------------------------------------------------ */
/*                           初期化 & export                           */
/* ------------------------------------------------------------------ */



export const supabase: SupabaseClient = createClient(
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: true,       // ✅ セッション永続化
      autoRefreshToken: true,     // ✅ トークン自動更新
      detectSessionInUrl: true,   // ✅ OAuth コールバック許可
    },
  }
);

// 🔎 DevTools から `supabase.auth.getSession()` 等を叩けるようにする
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.supabase = supabase;
}

/* ------------------------------------------------------------------ */
/*                             型定義                                 */
/* ------------------------------------------------------------------ */

export interface UserCredits {
  id?: number;
  user_id: string;
  plan_credits: number;
  max_plan_credits: number;
  charged_credits: number;
  monthly_usage:number;
  product_count: number;
  max_products: number;
  last_reset_date: string; // ISO 文字列
}

/* ------------------------------------------------------------------ */
/*                       users テーブル関連                          */
/* ------------------------------------------------------------------ */

/**
 * 指定ユーザーの users レコードを取得
 */
export async function getUserMemberData(userId: string) {
  if (!userId) return null;

  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.error('[getUserMemberData] Error:', error);
    return null;
  }
  return data;
}

/**
 * 管理者かどうか判定
 */
export async function isUserAdmin(userId: string): Promise<boolean> {
  if (!userId) return false;

  const { data, error } = await supabase
    .from('users')
    .select('role')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.error('[isUserAdmin] Error:', error);
    return false;
  }
  return data?.role === 'admin';
}

/* ------------------------------------------------------------------ */
/*                     user_credits テーブル関連                       */
/* ------------------------------------------------------------------ */

const defaultCredits = (userId: string): UserCredits => ({
  user_id: userId,
  plan_credits: 0,
  max_plan_credits: 0,
  charged_credits: 0,
  monthly_usage: 0,
  product_count: 0,
  max_products: 30,
  last_reset_date: new Date().toISOString(),
});

/**
 * クレジット情報を取得  
 * エラー時でも必ず「使える形」のオブジェクトを返す
 */
export async function getUserCreditsData(
  userId: string
): Promise<UserCredits> {
  if (!userId) return defaultCredits(userId);

  try {
    const { data, error } = await supabase
      .from('user_credits')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle<UserCredits>();

    if (error) {
      console.error('[getUserCreditsData] Error:', error);
      return defaultCredits(userId);
    }
    return data ?? defaultCredits(userId);
  } catch (err) {
    console.error('[getUserCreditsData] Exception:', err);
    return defaultCredits(userId);
  }
}

/**
 * クレジット情報を更新（行が無ければエラーを投げる）  
 * - 行の自動生成は DB の Trigger 側で行う想定
 */
export async function updateUserCredits(
  userId: string,
  updates: Partial<Omit<UserCredits, 'user_id'>>
): Promise<UserCredits | null> {
  if (!userId) return null;

  try {
    // First, try to update the existing record
    const { data: updateData, error: updateError } = await supabase
      .from('user_credits')
      .update(updates)
      .eq('user_id', userId)
      .select()
      .maybeSingle<UserCredits>();

    if (updateError) throw updateError;
    
    // If update succeeded and returned data, return it
    if (updateData) {
      return updateData;
    }
    
    // If no data returned, the record doesn't exist, so create it
    return await createUserCreditsRecord(userId, updates);
  } catch (err) {
    console.error('[updateUserCredits] Exception:', err);
    throw err;
  }
}

/**
 * Atomically decrements user AI credits
 * @param userId The user ID
 * @param type The type of credits to decrement ('plan' or 'charged')
 * @param amount The amount to decrement (default: 1)
 * @returns Promise<boolean> - true if successful, false if insufficient credits
 */
export async function decrementUserAICredits(
  userId: string,
  type: 'plan' | 'charged' = 'plan',
  amount: number = 1
): Promise<boolean> {
  if (!userId) return false;

  try {
    const column = type === 'plan' ? 'plan_credits' : 'charged_credits';
    
    // Use atomic SQL update to prevent race conditions
    const { data, error } = await supabase.rpc('decrement_ai_credits', {
      p_user_id: userId,
      p_column: column,
      p_amount: amount
    });

    if (error) {
      console.error(`[decrementUserAICredits] Error:`, error);
      return false;
    }

    return data === true;
  } catch (err) {
    console.error('[decrementUserAICredits] Exception:', err);
    return false;
  }
}
/**
 * Creates a new user credits record with default values and updates
 */
export async function createUserCreditsRecord(
  userId: string,
  updates: Partial<Omit<UserCredits, 'user_id'>> = {}
): Promise<UserCredits | null> {
  try {
    const { data, error } = await supabase
      .from('user_credits')
      .insert({
        ...defaultCredits(userId),
        ...updates
      })
      .select()
      .maybeSingle<UserCredits>();

    if (error) throw error;
    return data;
  } catch (err) {
    console.error('[createUserCreditsRecord] Exception:', err);
    throw err;
  }
}

/* ------------------------------------------------------------------ */
/*                        便利ユーティリティ                           */
/* ------------------------------------------------------------------ */

/**
 * SSR / Edge Functions で手動トークン更新したいとき用
 */
export async function refreshSession() {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error) {
    console.error('[refreshSession] Error:', error);
    return;
  }
  if (session) {
    await supabase.auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    });
  }
}
